import * as yup from "yup";

import {
  endDateValidationRule,
  genericDateRule,
  inProgress,
  inProgressDateRule,
  inProgressSimpleArrayRule,
  inProgressStringRule,
  inProgressWithBoolParentStringRule,
  nullableNumberRule,
  regionsRule,
  requiredDateRule,
  requiredStringRule,
  startDateValidationRule,
} from "../../../utils/validationUtils";

// TODO Lots of nullable()s in this one, this is bad practice
//  Backend should be updated to return empty strings or type equivalent rather than null for empty values.
//  Failing that, front-end would be better off setting better non-null `empty` values in Template Data Selectors.
//  This is also throwing a lot of red and yellow into the console.

const dueAt = inProgressDateRule("You must define the creative due date.");

/**
 * Fields shared between Brand and General
 * Simple and WebBanner will extend this object with their own fields and can overwrite the ones
 *  defined if they need to.
 */
const commonFields = yup.object({
  contentCreatorsDetails: yup.mixed(), // IUser[]
  endAt: endDateValidationRule,
  fileFormatDescription: yup.string().nullable(),
  isBuyGet: yup.boolean().nullable(),
  isCreativeReviewRequired: yup.boolean().nullable(),
  primaryMessage: yup.string().nullable(),
  secondaryMessage: yup.string().nullable(),
  status: yup.string().nullable(),
  startAt: startDateValidationRule,
});

/**
 * SIMPLE
 */
export const SimpleSchema = commonFields.shape({
  additionalCreativeDetails: yup.string().nullable(),
  assetType: inProgressStringRule("You must describe the asset type."),
  areVendorAssetsProvided: yup.boolean().nullable(),
  dueAt,

  primaryMessage: inProgressStringRule("You must describe primary message."),
  size: inProgressStringRule("You must describe the asset size."),

  vendorAssetsLocation: inProgressWithBoolParentStringRule(
    "areVendorAssetsProvided",
    "Please identify the assents location."
  ),
});

/**
 * WEB BANNER
 */
const position = yup.object().shape({
  id: yup.number().nullable(),
  assetType: yup.string(),
  desktopDimensions: yup.string().nullable(),
  mobileDimensions: yup.string().nullable(),
  tabletDimensions: yup.string().nullable(),
});

export const WebBannerSchema = commonFields.shape({
  cta: yup.string().nullable(),
  dueAt,
  isSegmented: yup.boolean().nullable(),
  legal: yup.string().nullable(),
  notesForCreative: yup.string().nullable(),
  notesForEcomm: yup.string().nullable(),

  positions: yup
    .array()
    .of(position)
    .when("status", {
      is: inProgress,
      then: (schema) => schema.min(1, "You must select at least on asset type"),
      otherwise: (schema) => schema,
    }),

  segments: inProgressWithBoolParentStringRule(
    "isSegmented",
    "You must specify the asset segments."
  ),

  sitePlacement: yup.string().nullable(),
});

/**
 * Dealer Advertising
 */
export const DealerAdSchema = commonFields.shape({
  additionalAssetsLocation: inProgressWithBoolParentStringRule(
    "areAdditionalAssetsProvided",
    "You must specify the location for Additional Assets."
  ),

  assetDescription: requiredStringRule("You must provide content direction"),
  copyDirection: yup.string().nullable(),
  colour: yup.string().nullable(),
  dueAt,
  endAt: endDateValidationRule,
  objectiveOrPurpose: yup.string().nullable(),
  startAt: startDateValidationRule,
});

/**
 * Paid Media
 */
export const PaidMediaSchema = commonFields.shape({
  adMedium: inProgressStringRule("You must select the ad medium"),
  adType: inProgressStringRule("You must select the ad type."),
  adFormat: inProgressStringRule("You must select the ad format."),
  adSpecifications: inProgressStringRule("You must describe the ad format."),
  assetLanguages: inProgressSimpleArrayRule(yup.number(), "You must select at least one language."),
  dueAt,
  projectOverview: requiredStringRule("You must provide asset creative direction."),
  regions: regionsRule,
  skuListUploadLocation: inProgressWithBoolParentStringRule(
    "isSkuListRequired",
    "Please provide upload location for SKU list."
  ),
  traffickingDueAt: requiredDateRule("You must define the trafficking due date."),
});

/**
 * EDM
 */
export const EdmSchema = commonFields.shape({
  deploymentAt: yup.date().when("status", {
    is: inProgress,
    then: genericDateRule.required("You must define the due date."),
    otherwise: genericDateRule,
  }),
  dueAt,
  fromPricing: inProgressStringRule("You must specify From Pricing."),
  description: inProgressStringRule("You must specify the description."),
  preHeaderDirection: inProgressStringRule("You must specify the pre-header direction."),
  subjectLineDirection: inProgressStringRule("You must specify the subject line direction."),
});

/**
 * Merch Digital
 */
export const MerchDigitalSchema = commonFields.shape({
  assetType: inProgressStringRule("You must select the asset type."),
  contentDirection: requiredStringRule("You must provide content direction"),
  dueAt,
  englishNumberOfScreens: nullableNumberRule,
  frenchNumberOfScreens: nullableNumberRule,
});

/**
 * Merch Print
 */
export const MerchPrintSchema = commonFields.shape({
  assetType: inProgressStringRule("You must select the asset type."),
  contentDirection: requiredStringRule("You must provide content direction"),
  dueAt,
  englishNumberOfStores: nullableNumberRule,
  englishQtyPerStore: nullableNumberRule,
  frenchNumberOfStores: nullableNumberRule,
  frenchQtyPerStore: nullableNumberRule,
  warehouseEta: genericDateRule,
});
